<template>
  <div>

    <!-- Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">My Loans</h5>
        <p class="page-sub">Apply for a loan and track your repayment progress</p>
      </div>
      <button class="btn-primary-action" @click="openApplyModal" :disabled="!employeeId">
        <font-awesome-icon icon="plus" /> Apply for Loan
      </button>
    </div>

    <!-- No employee profile linked -->
    <div v-if="!employeeId" class="alert-box alert-box--warn">
      <font-awesome-icon icon="exclamation-triangle" />
      Your account is not linked to an employee profile. Contact your HR administrator.
    </div>

    <template v-else>
      <!-- Skeleton -->
      <div v-if="loading" class="sk-table">
        <div class="sk-row-head"></div>
        <div v-for="n in 4" :key="n" class="sk-row"></div>
      </div>

      <!-- Empty state -->
      <div v-else-if="!applications.length" class="empty-box">
        <div class="empty-illustration">
          <font-awesome-icon icon="hand-holding-usd" class="empty-icon" />
        </div>
        <p class="empty-title">No loan applications yet</p>
        <p class="empty-sub">Click "Apply for Loan" to submit your first application.</p>
      </div>

      <!-- Applications list -->
      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr>
              <th>Loan Type</th>
              <th>Amount</th>
              <th>Monthly EMI</th>
              <th>Tenure</th>
              <th>Paid</th>
              <th>Outstanding</th>
              <th>Status</th>
              <th>Applied On</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="app in applications" :key="app.id" class="clickable-row" @click="viewDetail(app)">
              <td class="fw-600">{{ app.loan_type?.name }}</td>
              <td>{{ fmt(app.amount_requested) }}</td>
              <td>{{ fmt(app.emi_amount) }}</td>
              <td>{{ app.repayment_type?.label || app.installments_total + ' instalment(s)' }}</td>
              <td>
                <span class="progress-text">{{ app.installments_paid }} / {{ app.installments_total }}</span>
                <div class="progress-bar-wrap">
                  <div class="progress-bar-fill" :style="{ width: progressPct(app) + '%' }"></div>
                </div>
              </td>
              <td>{{ fmt(app.outstanding_balance) }}</td>
              <td><span class="chip" :class="statusClass(app.status)">{{ statusLabel(app.status) }}</span></td>
              <td>{{ fmtDate(app.created_at) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination" v-if="meta.last_page > 1">
          <button class="page-btn" :disabled="meta.current_page === 1" @click="goPage(meta.current_page - 1)">‹</button>
          <span class="page-info">{{ meta.current_page }} / {{ meta.last_page }}</span>
          <button class="page-btn" :disabled="meta.current_page === meta.last_page" @click="goPage(meta.current_page + 1)">›</button>
        </div>
      </div>
    </template>

    <!-- ══  Apply for Loan Modal  ══ -->
    <div class="modal-overlay" v-if="applyModal.open" @click.self="applyModal.open = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">Apply for Loan</p>
          <button class="modal-close" @click="applyModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Loan Type <span class="req">*</span></label>
            <select class="form-input" v-model="applyModal.loan_type_id" @change="onLoanTypeChange">
              <option value="">— select —</option>
              <option v-for="lt in activeLoanTypes" :key="lt.id" :value="lt.id">{{ lt.name }}</option>
            </select>
          </div>

          <div v-if="applyModal.selectedType" class="type-info-box">
            <span v-if="applyModal.selectedType.description">{{ applyModal.selectedType.description }}</span>
            <span v-if="applyModal.selectedType.min_amount > 0"> Minimum: {{ fmt(applyModal.selectedType.min_amount) }}.</span>
            <span v-if="applyModal.selectedType.max_amount"> Maximum: {{ fmt(applyModal.selectedType.max_amount) }}.</span>
            <span v-if="applyModal.selectedType.eligibility_months > 0"> Requires {{ applyModal.selectedType.eligibility_months }} month(s) of service.</span>
          </div>

          <div class="form-row">
            <label class="form-label">Repayment Tenure <span class="req">*</span></label>
            <select class="form-input" v-model="applyModal.loan_repayment_type_id" @change="calcEmi">
              <option value="">— select tenure —</option>
              <option v-for="r in applyModal.repaymentOptions" :key="r.id" :value="r.id">
                {{ r.label }} ({{ r.installments }} instalment(s))
              </option>
            </select>
          </div>

          <div class="form-row">
            <label class="form-label">Amount Requested <span class="req">*</span></label>
            <input type="number" class="form-input" v-model="applyModal.amount_requested" min="1" placeholder="0.00" @input="calcEmi" />
          </div>

          <div v-if="applyModal.emi > 0" class="emi-preview">
            <font-awesome-icon icon="calculator" />
            Estimated monthly deduction: <strong>{{ fmt(applyModal.emi) }}</strong>
          </div>

          <div class="form-row">
            <label class="form-label">Notes / Reason</label>
            <textarea class="form-input" rows="3" v-model="applyModal.notes" placeholder="Optional reason or supporting details…"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="applyModal.open = false">Cancel</button>
          <button class="btn-primary-action" :disabled="saving" @click="submitLoan">
            {{ saving ? 'Submitting…' : 'Submit Application' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══  Loan Detail Drawer / Modal  ══ -->
    <div class="modal-overlay" v-if="detailModal.open" @click.self="detailModal.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <p class="modal-title">Loan Details</p>
          <button class="modal-close" @click="detailModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" v-if="detailModal.loan">
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Loan Type</span>
              <span class="detail-value fw-600">{{ detailModal.loan.loan_type?.name }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Status</span>
              <span class="chip" :class="statusClass(detailModal.loan.status)">{{ statusLabel(detailModal.loan.status) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Amount Requested</span>
              <span class="detail-value">{{ fmt(detailModal.loan.amount_requested) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Monthly EMI</span>
              <span class="detail-value">{{ fmt(detailModal.loan.emi_amount) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Tenure</span>
              <span class="detail-value">{{ detailModal.loan.repayment_type?.label || detailModal.loan.installments_total + ' instalments' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Instalments Paid</span>
              <span class="detail-value">{{ detailModal.loan.installments_paid }} / {{ detailModal.loan.installments_total }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Outstanding Balance</span>
              <span class="detail-value fw-600">{{ fmt(detailModal.loan.outstanding_balance) }}</span>
            </div>
            <div class="detail-item" v-if="detailModal.loan.disbursement_date">
              <span class="detail-label">Disbursement Date</span>
              <span class="detail-value">{{ fmtDate(detailModal.loan.disbursement_date) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Applied On</span>
              <span class="detail-value">{{ fmtDate(detailModal.loan.created_at) }}</span>
            </div>
          </div>

          <div v-if="detailModal.loan.notes" class="notes-box">
            <p class="notes-label">Your Notes</p>
            <p class="notes-content">{{ detailModal.loan.notes }}</p>
          </div>

          <div v-if="detailModal.loan.review_note" class="review-note-box">
            <p class="notes-label">Reviewer Note</p>
            <p class="notes-content">{{ detailModal.loan.review_note }}</p>
          </div>

          <div v-if="['disbursed','active'].includes(detailModal.loan.status)">
            <p class="section-sub-title">Repayment Progress</p>
            <div class="progress-row">
              <div class="progress-bar-wrap progress-bar-wrap--lg">
                <div class="progress-bar-fill" :style="{ width: progressPct(detailModal.loan) + '%' }"></div>
              </div>
              <span class="progress-pct">{{ progressPct(detailModal.loan) }}%</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-primary-action" @click="detailModal.open = false">Close</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'MyLoansPage',
  data() {
    return {
      loading: false,
      saving: false,
      applications: [],
      meta: { current_page: 1, last_page: 1 },
      page: 1,
      loanTypes: [],

      applyModal: {
        open: false,
        loan_type_id: '',
        loan_repayment_type_id: '',
        amount_requested: '',
        notes: '',
        selectedType: null,
        repaymentOptions: [],
        emi: 0,
      },
      detailModal: { open: false, loan: null },
    };
  },

  computed: {
    employeeId() {
      return this.$store?.state?.getLoggedInUser?.employee_id || null;
    },
    activeLoanTypes() {
      return this.loanTypes.filter(lt => lt.is_active);
    },
  },

  mounted() {
    if (this.employeeId) {
      this.fetchApplications();
    }
    this.fetchLoanTypes();
  },

  methods: {
    async fetchApplications() {
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/loan', {
          params: { employee_id: this.employeeId, length: 15, page: this.page, context: 'self-service' },
        });
        this.applications = data.data?.data || [];
        this.meta = { current_page: data.data?.current_page || 1, last_page: data.data?.last_page || 1 };
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load loans');
      } finally {
        this.loading = false;
      }
    },
    goPage(p) {
      this.page = p;
      this.fetchApplications();
    },
    async fetchLoanTypes() {
      try {
        const { data } = await axios.get('/hr/loan-type');
        this.loanTypes = data.data || [];
      } catch { /* silent */ }
    },
    openApplyModal() {
      this.applyModal = {
        open: true, loan_type_id: '', loan_repayment_type_id: '',
        amount_requested: '', notes: '', selectedType: null, repaymentOptions: [], emi: 0,
      };
    },
    onLoanTypeChange() {
      const selected = this.loanTypes.find(lt => lt.id === this.applyModal.loan_type_id);
      this.applyModal.selectedType = selected || null;
      this.applyModal.repaymentOptions = (selected?.repayment_types || []).filter(r => r.is_active);
      this.applyModal.loan_repayment_type_id = '';
      this.applyModal.emi = 0;
    },
    calcEmi() {
      const amount = parseFloat(this.applyModal.amount_requested) || 0;
      const repayment = this.applyModal.repaymentOptions.find(r => r.id === this.applyModal.loan_repayment_type_id);
      this.applyModal.emi = (repayment?.installments > 0)
        ? Math.round((amount / repayment.installments) * 100) / 100
        : 0;
    },
    async submitLoan() {
      if (!this.applyModal.loan_type_id || !this.applyModal.loan_repayment_type_id || !this.applyModal.amount_requested) {
        this.$toast?.error('Loan type, tenure, and amount are required');
        return;
      }
      this.saving = true;
      try {
        await axios.post('/hr/loan', {
          employee_id:            this.employeeId,
          loan_type_id:           this.applyModal.loan_type_id,
          loan_repayment_type_id: this.applyModal.loan_repayment_type_id,
          amount_requested:       this.applyModal.amount_requested,
          notes:                  this.applyModal.notes,
        });
        this.applyModal.open = false;
        this.$toast?.success('Loan application submitted');
        this.fetchApplications();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Submission failed');
      } finally {
        this.saving = false;
      }
    },
    viewDetail(app) {
      this.detailModal = { open: true, loan: app };
    },

    // helpers
    progressPct(app) {
      if (!app.installments_total) return 0;
      return Math.min(100, Math.round((app.installments_paid / app.installments_total) * 100));
    },
    fmt(v) {
      return v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
    },
    fmtDate(d) {
      return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    },
    statusLabel(s) {
      return { pending_approval: 'Pending Approval', approved: 'Approved', rejected: 'Rejected', disbursed: 'Disbursed', active: 'Active', closed_repaid: 'Repaid', closed_settled: 'Settled' }[s] || s;
    },
    statusClass(s) {
      return { pending_approval: 'chip--amber', approved: 'chip--blue', rejected: 'chip--red', disbursed: 'chip--purple', active: 'chip--green', closed_repaid: 'chip--gray', closed_settled: 'chip--gray' }[s] || 'chip--gray';
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline  { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }

.alert-box { display:flex; align-items:center; gap:10px; padding:14px 16px; border-radius:10px; font-size:13px; font-weight:500; }
.alert-box--warn { background:#fef9c3; border:1px solid #fde047; color:#854d0e; }

.table-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table  { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:12px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .1s; }
.data-table tbody tr.clickable-row { cursor:pointer; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 14px; color:#1a2b45; vertical-align:middle; }
.fw-600 { font-weight:600; }

.progress-text { font-size:11px; color:#7a90b0; display:block; margin-bottom:3px; }
.progress-bar-wrap { height:6px; background:#e4eaf2; border-radius:10px; overflow:hidden; }
.progress-bar-fill { height:100%; background:#004AAD; border-radius:10px; transition:width .3s ease; }
.progress-bar-wrap--lg { height:10px; flex:1; }
.progress-row { display:flex; align-items:center; gap:12px; margin-top:6px; }
.progress-pct { font-size:12px; font-weight:600; color:#004AAD; min-width:36px; }

.chip { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fee2e2; color:#dc2626; }
.chip--blue   { background:#dbeafe; color:#1d4ed8; }
.chip--purple { background:#ede9fe; color:#7c3aed; }
.chip--gray   { background:#f1f5f9; color:#64748b; }

.pagination { display:flex; align-items:center; justify-content:center; gap:12px; padding:14px; border-top:1px solid #f0f4f8; }
.page-btn   { background:#fff; border:1px solid #dde5f0; border-radius:6px; padding:4px 12px; font-size:15px; cursor:pointer; }
.page-btn:disabled { opacity:.4; cursor:not-allowed; }
.page-info  { font-size:13px; color:#7a90b0; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row-head,.sk-row { height:44px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
.sk-row-head { height:40px; background:#f8fafc; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.empty-box         { display:flex; flex-direction:column; align-items:center; padding:80px 20px; gap:14px; }
.empty-illustration{ width:96px; height:96px; border-radius:50%; background:#f0f7ff; display:flex; align-items:center; justify-content:center; }
.empty-icon        { font-size:40px; color:#004AAD; opacity:.5; }
.empty-title       { font-size:18px; font-weight:700; color:#1a2b45; margin:0; }
.empty-sub         { font-size:13px; color:#7a90b0; margin:0; text-align:center; max-width:380px; line-height:1.6; }

/* Modals */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box     { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--wide { max-width:640px; }
.modal-head    { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title   { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close   { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; }
.modal-body    { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer  { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }
.form-row      { display:flex; flex-direction:column; gap:5px; }
.form-label    { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input    { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; }
.form-input:focus { border-color:#004AAD; }
.req { color:#dc2626; }

.type-info-box { background:#f0f7ff; border:1px solid #bfdbfe; border-radius:8px; padding:8px 12px; font-size:12px; color:#1e40af; line-height:1.6; }
.emi-preview   { background:#f0fdf4; border:1px solid #bbf7d0; border-radius:8px; padding:10px 14px; font-size:13px; color:#15803d; display:flex; align-items:center; gap:8px; }

.detail-grid   { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.detail-item   { display:flex; flex-direction:column; gap:3px; }
.detail-label  { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; }
.detail-value  { font-size:14px; color:#1a2b45; }

.notes-box, .review-note-box { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:12px 14px; }
.review-note-box { background:#fff7ed; border-color:#fdba74; }
.notes-label   { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin:0 0 6px; }
.notes-content { font-size:13px; color:#1a2b45; margin:0; line-height:1.6; }

.section-sub-title { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#4a5e78; margin:0; }
</style>
